import React, { useEffect, useState } from 'react';
import { Box } from 'lucide-react';
import { ImageGallery } from './ImageGallery';
import { useCurrentMessage } from '@/contexts/CurrentMessageContext';
import { useConversation } from '@/services/conversationService';
import Loader from './Loader';
import { OpenSCADViewer } from './OpenSCADViewer';
import { MeshViewer } from './MeshViewer';
import { MeshError } from './MeshError';
import {
  isTerminalFailure,
  useMeshJobProgress,
} from '@/hooks/useMeshJobProgress';
import { startMeshFromImage } from '@/services/meshGenerationService';
import { useIsLoading } from '@/services/messageService';
import { useTranslation } from 'react-i18next';
import { getDownloadURL, ref } from 'firebase/storage';
import { storage, firestore } from '@/lib/firebase';
import { doc, setDoc, deleteField } from 'firebase/firestore';

export function ViewerSection() {
  const { t } = useTranslation('common');
  const isLoading = useIsLoading();
  const { currentMessage: message } = useCurrentMessage();
  const { conversation } = useConversation();
  const job = useMeshJobProgress({
    conversationId: conversation?.id,
    taskId: conversation?.pendingMeshId,
  });

  const [meshUrl, setMeshUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!conversation?.currentMeshPath) {
      setMeshUrl(null);
      return;
    }
    let cancelled = false;
    void getDownloadURL(ref(storage, conversation.currentMeshPath)).then(
      (url) => {
        if (!cancelled) setMeshUrl(url);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [conversation?.currentMeshPath, conversation?.currentMeshId]);

  const handleDismiss = async () => {
    if (!conversation?.id) return;
    await setDoc(
      doc(firestore, 'conversations', conversation.id),
      { pendingMeshId: deleteField() },
      { merge: true },
    );
  };

  const handleRetry = async () => {
    if (!conversation?.id || !conversation.sourceImageId) return;
    await startMeshFromImage({
      conversationId: conversation.id,
      imageId: conversation.sourceImageId,
    });
  };

  // 1. Pending in-flight → use the shared <Loader> for parity with the
  //    SCAD viewer (animated dots, rotates to a Japanese rendering
  //    message after 2s). We don't surface numeric progress anymore.
  if (
    conversation?.pendingMeshId &&
    job &&
    !isTerminalFailure(job.status) &&
    job.status !== 'success'
  ) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-synergy-neutral-900">
        <Loader message="Generating mesh" />
      </div>
    );
  }

  // 2. Pending but terminal failure → error UI
  if (conversation?.pendingMeshId && job && isTerminalFailure(job.status)) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-synergy-neutral-900">
        <MeshError
          status={job.status}
          errorMsg={job.errorMsg}
          errorCode={job.errorCode}
          onRetry={() => void handleRetry()}
          onDismiss={() => void handleDismiss()}
        />
      </div>
    );
  }

  // 3. Mesh ready — wrap in an error boundary so a GLB-load failure
  //    surfaces a friendly fallback instead of crashing the panel.
  if (conversation?.mode === 'mesh' && conversation.currentMeshPath && meshUrl) {
    return (
      <div className="flex h-full w-full bg-synergy-neutral-900">
        <MeshViewerErrorBoundary>
          <MeshViewer src={meshUrl} />
        </MeshViewerErrorBoundary>
      </div>
    );
  }

  // 3b. Mesh conversation but the GLB isn't (yet) loadable — either
  //    `currentMeshPath` is missing or `getDownloadURL` hasn't resolved.
  //    Show a dedicated mesh-empty state instead of leaking the SCAD
  //    "Send a prompt..." copy into a mesh conversation.
  if (conversation?.mode === 'mesh') {
    return (
      <div className="flex h-full w-full items-center justify-center bg-synergy-neutral-900">
        <MeshUnavailable />
      </div>
    );
  }

  // 4. SCAD path (existing behavior)
  const hasContent =
    (message?.content.images && Array.isArray(message.content.images)) ||
    message?.content.artifact?.code;

  return (
    <div className="flex h-full w-full items-center justify-center bg-synergy-neutral-900">
      {isLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader message={t('viewer.generating')} />
        </div>
      ) : hasContent ? (
        <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-2">
          {message?.content.images && Array.isArray(message.content.images) && (
            <ImageGallery imageIds={message.content.images} />
          )}
          {message?.content.artifact?.code && <OpenSCADViewer />}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3 text-synergy-text-tertiary">
          <Box className="h-7 w-7 text-synergy-text-tertiary" />
          <p className="text-sm text-synergy-text-tertiary">
            {t('viewer.empty')}
          </p>
        </div>
      )}
    </div>
  );
}

/** Fallback shown when a mesh conversation has no usable GLB yet —
 *  either the path is unset, the download URL hasn't resolved, or the
 *  three-fiber viewer threw while loading. Encourages the user to
 *  retry from the chat panel rather than the SCAD-empty copy.  */
function MeshUnavailable({ onReload }: { onReload?: () => void }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col items-center gap-3 text-synergy-text-tertiary">
      <Box className="h-7 w-7 text-synergy-text-tertiary" />
      <p className="max-w-xs text-center text-sm text-synergy-text-tertiary">
        {t('meshUnavailable.message', {
          defaultValue:
            "Mesh isn't available right now. Try regenerating from the chat panel.",
        })}
      </p>
      {onReload && (
        <button
          type="button"
          onClick={onReload}
          className="rounded-md border border-synergy-neutral-700 bg-synergy-neutral-800 px-3 py-1 text-xs font-bold text-synergy-text-secondary transition-colors hover:border-synergy-blue hover:text-synergy-blue"
        >
          {t('meshUnavailable.reloadButton', { defaultValue: 'Reload' })}
        </button>
      )}
    </div>
  );
}

/** Tiny error boundary around <MeshViewer>. GLB-load failures inside
 *  three-fiber's Suspense throw asynchronously; this catches them and
 *  shows the same friendly fallback as the missing-GLB branch above,
 *  plus a "Reload" button that resets the boundary so the next render
 *  retries the load. */
class MeshViewerErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    // Surface GLB-load failures in the dev console; production users
    // see only the friendly fallback below.
    console.error('[MeshViewerErrorBoundary] caught:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <MeshUnavailable onReload={() => this.setState({ hasError: false })} />
        </div>
      );
    }
    return this.props.children;
  }
}
